
import { createAction } from '../../utils/reduxUtils';
import { DialogConfig, SnackbarConfig } from './models';
import { IFirebaseUser, ITour, ITourProgress, TourCode } from '../../models';
import { IAsyncError, ICreateUpdateTourReviewRequest } from '../../common';

interface CreateTourReviewRequest extends ICreateUpdateTourReviewRequest {
  tourCode: string;
  silent: boolean;
  onSuccess?: () => void;
}

const initApp = createAction('@app/INIT')<void>;

const showDialog = createAction('@common/SHOW_DIALOG')<DialogConfig>;

const hideDialog = createAction('@common/HIDE_DIALOG')<boolean | undefined>;

const showSnackbar = createAction('@common/SHOW_SNACKBAR')<SnackbarConfig>;

const hideSnackbar = createAction('@common/HIDE_SNACKBAR')<void>;

const allowLocationTracking = createAction('@common/ALLOW_LOCATION_TRACKING')<boolean>;

const fetchTourAsync = {
  request: createAction('@common/FETCH_TOUR_REQUEST')<string>,
  success: createAction('@common/FETCH_TOUR_SUCCESS')<ITour>,
  failure: createAction('@common/FETCH_TOUR_FAILURE')<IAsyncError>,
  cancel: createAction('@common/FETCH_TOUR_CANCEL')<void>,
};

const removeTour = createAction('@common/REMOVE_TOUR')<string>;

const cancelTour = {
  request: createAction('@common/CANCEL_TOUR_REQUEST')<{
    tourCode: string;
    onConfirmed?: () => void;
  }>,
  confirmed: createAction('@common/CANCEL_TOUR_CONFIRMED')<{
    tourCode: string;
    tourStop?: number;
  }>,
};

const clearAssetCache = createAction('@common/CLEAR_ASSET_CACHE')<string>;

const deleteTour = {
  request: createAction('@common/DELETE_TOUR_REQUEST')<string>,
  success: createAction('@common/DELETE_TOUR_SUCCESS')<string>,
  failure: createAction('@common/DELETE_TOUR_FAILURE')<IAsyncError>,
};

const synchronizeTourProgress = {
  request: createAction('@common/SYNC_TOUR_PROGRESS_REQUEST')<void>,
  success: createAction('@common/SYNC_TOUR_PROGRESS_SUCCESS')<ITourProgress[]>,
  failure: createAction('@common/SYNC_TOUR_PROGRESS_FAILURE')<IAsyncError>,
  cancel: createAction('@common/SYNC_TOUR_PROGRESS_CANCEL')<void>,
};

const signIn = {
  request: createAction('@common/SIGN_IN_REQUEST')<void>,
  success: createAction('@common/SIGN_IN_SUCCESS')<IFirebaseUser>,
  failure: createAction('@common/SIGN_IN_FAILURE')<IAsyncError>,
  cancel: createAction('@common/SIGN_IN_CANCEL')<void>,
};

const signInAnonymously = createAction('@common/SIGN_IN_ANONYMOUSLY_REQUEST')<void>;

const signOut = {
  request: createAction('@common/SIGN_OUT_REQUEST')<void>,
  success: createAction('@common/SIGN_OUT_SUCCESS')<void>,
  failure: createAction('@common/SIGN_OUT_FAILURE')<IAsyncError>,
};

const connection = {
  online: createAction('@common/ONLINE')<void>,
  offline: createAction('@common/OFFLINE')<void>,
};

const updateUser = createAction('@common/UPDATE_USER')<IFirebaseUser>;

// clear user from redux store and clear-up potential remains of firebase user
const clearUser = createAction('@common/CLEAR_USER')<void>;

const updateAuthorities = createAction('@common/UPDATE_AUTHORITIES')<void>;

const reauthenticate = {
  showDialog: createAction('@common/REAUTHENTICATE_SHOW')<void>,
  request: createAction('@common/REAUTHENTICATE_REQUEST')<string>,
  success: createAction('@common/REAUTHENTICATE_SUCCESS')<void>,
  failure: createAction('@common/REAUTHENTICATE_FAILURE')<IAsyncError>,
  cancel: createAction('@common/REAUTHENTICATE_CANCEL')<void>,
};

const resetPassword = {
  request: createAction('@common/RESET_PASSWORD_REQUEST')<void>,
  success: createAction('@common/RESET_PASSWORD_SUCCESS')<void>,
  failure: createAction('@common/RESET_PASSWORD_FAILURE')<IAsyncError>,
};

const completeCreateTourOnboarding = createAction('@common/COMPLETE_CREATE_TOUR_ONBOARDING')<void>;

const createTourReview = {
  request: createAction('@common/CREATE_TOUR_REVIEW_REQUEST')<CreateTourReviewRequest>,
  success: createAction('@common/CREATE_TOUR_REVIEW_SUCCESS')<TourCode>,
  failure: createAction('@common/CREATE_TOUR_REVIEW_FAILURE')<IAsyncError>,
  cancel: createAction('@common/CREATE_TOUR_REVIEW_CANCEL')<void>,
};

const actions = {
  initApp,
  showDialog,
  hideDialog,
  showSnackbar,
  hideSnackbar,
  allowLocationTracking,
  fetchTourAsync,
  removeTour,
  cancelTour,
  deleteTour,
  synchronizeTourProgress,
  signIn,
  signOut,
  clearUser,
  connection,
  updateUser,
  reauthenticate,
  resetPassword,
  completeCreateTourOnboarding,
  createTourReview,
  clearAssetCache,
  signInAnonymously,
  updateAuthorities,
};

export default actions;
